import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { ArrowRight, Instagram, Phone, Mail, MapPin } from "lucide-react";
import { SITE, STATS, VIDEO_REELS } from "../lib/site";
import ReelCard from "../components/ReelCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { first_name: "", last_name: "", phone: "", email: "", description: "" };

// Animated logo wordmark
function LogoMark() {
  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={{ show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } } }}
      className="font-display uppercase leading-[0.82] tracking-tight text-foreground"
      data-testid="hero-logo-mark"
    >
      {/* Row 1: BE */}
      <div className="flex items-baseline gap-[0.04em] text-[28vw] md:text-[14vw] lg:text-[12vw] whitespace-nowrap">
        {"BE".split("").map((c, i) => (
          <motion.span
            key={`be-${i}`}
            variants={{ hidden: { y: "100%", opacity: 0 }, show: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}
            className="inline-block overflow-hidden"
          >
            {c}
          </motion.span>
        ))}
      </div>
      {/* Row 2: GREAT */}
      <div className="flex items-baseline gap-[0.02em] text-[28vw] md:text-[14vw] lg:text-[12vw] -mt-[2vw] whitespace-nowrap">
        {"GREAT".split("").map((c, i) => (
          <motion.span
            key={`g-${i}`}
            variants={{ hidden: { y: "100%", opacity: 0 }, show: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}
            className="inline-block overflow-hidden text-primary"
          >
            {c}
          </motion.span>
        ))}
        <motion.span
          variants={{ hidden: { scale: 0, opacity: 0 }, show: { scale: 1, opacity: 1, transition: { duration: 0.5, delay: 0.2 } } }}
          className="inline-block w-[0.18em] h-[0.18em] bg-primary rounded-full ml-[0.05em] align-baseline"
          aria-hidden
        />
      </div>
    </motion.div>
  );
}

// Animated basketball — rotating ball with seam lines via SVG
function BasketballMotion() {
  return (
    <motion.svg
      viewBox="0 0 200 200"
      className="w-full h-full"
      animate={{ rotate: 360 }}
      transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
    >
      <defs>
        <radialGradient id="bball" cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#FF7A2E" />
          <stop offset="60%" stopColor="#FF5C00" />
          <stop offset="100%" stopColor="#C24400" />
        </radialGradient>
      </defs>
      <circle cx="100" cy="100" r="92" fill="url(#bball)" stroke="#0A0A0A" strokeWidth="3" />
      <path d="M100 8 V192" stroke="#0A0A0A" strokeWidth="3" fill="none" />
      <path d="M8 100 H192" stroke="#0A0A0A" strokeWidth="3" fill="none" />
      <path d="M22 36 Q100 100 22 164" stroke="#0A0A0A" strokeWidth="3" fill="none" />
      <path d="M178 36 Q100 100 178 164" stroke="#0A0A0A" strokeWidth="3" fill="none" />
    </motion.svg>
  );
}

export default function Home() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thanks for submitting! We'll be in touch.");
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="home-page">
      <Toaster position="top-center" richColors />

      {/* HERO — Animated logo + basketball */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-12 bg-primary" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-primary">
              Basketball Training · Mankato MN
            </span>
          </motion.div>

          <div className="relative">
            {/* Spinning basketball positioned behind the wordmark */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-0 top-[2vw] md:top-[3vw] w-[36vw] md:w-[22vw] lg:w-[18vw] aspect-square z-0 pointer-events-none"
              aria-hidden
            >
              <BasketballMotion />
            </motion.div>

            <div className="relative z-10">
              <LogoMark />
            </div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-10 max-w-xl text-base md:text-lg text-foreground/75 leading-relaxed"
          >
            {SITE.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#inquiry"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-foreground text-background px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] hover:bg-primary transition-colors"
            >
              Train With Us
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#reels"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-2 py-4 text-xs font-bold uppercase tracking-[0.25em] border-b-2 border-foreground hover:border-primary hover:text-primary transition-colors"
            >
              Watch Reels
            </a>
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-instagram"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-foreground/70 hover:text-primary transition-colors ml-auto"
            >
              <Instagram size={16} /> {SITE.instagramHandle}
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-3 border-y border-foreground/15"
            data-testid="hero-stats"
          >
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className={`py-6 px-2 md:px-6 ${i !== 0 ? "sm:border-l border-foreground/15" : ""} ${i !== 0 ? "border-t sm:border-t-0 border-foreground/15" : ""}`}
              >
                <div className="font-display text-4xl md:text-5xl leading-none">{s.value}</div>
                <div className="mt-2 text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* POSITIONING — HOW WE TRAIN */}
      <section data-testid="home-positioning" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-x-0 gap-y-8 md:gap-14">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">How We Train</span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.9] tracking-tight">
              High-level <span className="text-primary">skill</span> and basketball teachers.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7">
            <p className="text-foreground/80 text-base md:text-lg leading-relaxed">
              BeGreat coaches have years of experience in skill development at both the youth and college level, making us the premier training spot in the Mankato area for players of all skill levels. We teach every skill in detail, then put it into game-like situations built from that experience. Connecting both skill enhancement and application into game-like scenarios is what our team specializes in.
            </p>
            <p className="mt-5 text-foreground/70 text-base md:text-lg leading-relaxed">
              Our team is made up of college coaches and skill development teachers, former high-level players with teaching experience, and coaches that have had proven results with all ages.
            </p>
          </div>
        </div>
      </section>

      {/* HENNEN QUOTE — Endorsement from Shane Hennen */}
      <section data-testid="home-hennen-quote" className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-12 gap-x-0 gap-y-8 md:gap-14 items-center"
          >
            <div className="col-span-12 md:col-span-5 order-2 md:order-1">
              <div className="relative overflow-hidden border border-foreground/15 bg-foreground max-w-sm md:max-w-none mx-auto md:mx-0" data-testid="home-hennen-image">
                <img
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_hoops-luxe/artifacts/yy8th5wk_SHANE%20HENNEN%20PIC.jpeg"
                  alt="Instagram story from Shane Hennen (hennen_workouts) endorsing BeGreat Basketball Training"
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="col-span-12 md:col-span-7 order-1 md:order-2 relative">
              <div className="absolute -top-6 md:-top-12 left-0 font-display text-8xl md:text-[10rem] leading-none text-primary/25 select-none pointer-events-none">"</div>
              <div className="flex items-center gap-3 mb-5 relative">
                <span className="h-px w-10 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Industry Endorsement</span>
              </div>
              <blockquote className="font-display uppercase text-3xl sm:text-4xl md:text-5xl leading-[1.02] tracking-tight relative">
                "Some of y'all know this, but for those that don't… <span className="text-primary">this dude is about to be one of the best in the industry.</span> Go follow @begreatbasketballtraining."
              </blockquote>
              <figcaption className="mt-8 flex flex-col gap-1">
                <span className="font-display uppercase text-xl md:text-2xl leading-none">— Shane Hennen</span>
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-foreground/60">World-Renowned Basketball Trainer</span>
              </figcaption>
            </div>
          </motion.div>
        </div>
      </section>


      {/* 4-STEP PROCESS */}
      <section data-testid="home-process" className="py-20 md:py-28 bg-secondary/40 border-y border-foreground/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 items-end mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">The BeGreat Process</span>
              </div>
              <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.9] tracking-tight">
                Four steps. <span className="text-primary">One outcome.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-foreground/10 border border-foreground/10">
            {[
              { n: "01", t: "Build", b: "Learn the footwork and details of the skill." },
              { n: "02", t: "Apply", b: "Use it on the move with guided defense." },
              { n: "03", t: "Read", b: "Make decisions off the defender's reaction." },
              { n: "04", t: "Play", b: "Live 1-on-1 and small-sided games where it counts." },
            ].map((step, i) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`home-process-step-${step.n}`}
                className="bg-background p-7 md:p-8 group hover:bg-foreground hover:text-background transition-colors"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-6">Step {step.n}</div>
                <div className="font-display uppercase text-4xl md:text-5xl leading-none">{step.t}</div>
                <p className="mt-5 text-sm md:text-base text-foreground/75 group-hover:text-background/80 leading-relaxed">
                  {step.b}
                </p>
              </motion.div>
            ))}
          </div>

          <p className="mt-8 text-center text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-foreground/55">
            No gimmicks. No dead time. Every drill answers one question: when does this show up in a game?
          </p>
        </div>
      </section>

      {/* WE MEET PLAYERS WHERE THEY ARE */}
      <section data-testid="home-programs" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-x-0 gap-y-8 md:gap-14">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Every Player. Every Level.</span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.9] tracking-tight">
              Programs to meet your player <span className="text-primary">where they are.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7">
            <p className="text-foreground/80 text-base md:text-lg leading-relaxed">
              Every BeGreat player is placed in a group at their level — from a third grader learning fundamentals to a college recruit sharpening scoring against live pressure. We cap every group at <span className="font-bold text-foreground">5 players</span> so every rep gets coached, and every player has a clear plan for what comes next.
            </p>

            <ul className="mt-8 space-y-4">
              {[
                "Fundamentals and confidence",
                "Skill development and game reads",
                "Advanced scoring and live competition",
              ].map((p) => (
                <li key={p} className="flex items-center gap-4">
                  <span className="h-3 w-3 bg-primary shrink-0" />
                  <span className="text-base md:text-lg font-medium">{p}</span>
                </li>
              ))}
            </ul>

            <a
              href="#inquiry"
              data-testid="home-programs-cta"
              className="mt-10 inline-flex items-center gap-2 bg-foreground text-background px-6 py-4 text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-primary transition-colors"
            >
              Find Your Player's Program <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* PROOF BAND */}
      <section data-testid="home-proof" className="py-20 md:py-28 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">The Proof</span>
              </div>
              <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.9] tracking-tight">
                Numbers that <span className="text-primary">back it up.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-background/15">
            {[
              { v: "1,500+", l: "Athletes Trained" },
              { v: "50+", l: "College Athletes Trained" },
              { v: "Max 5", l: "Players Per Group" },
              { v: "10+", l: "Years Developing Players" },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`home-proof-tile-${i}`}
                className="bg-foreground p-8 md:p-10"
              >
                <div className="font-display uppercase text-4xl md:text-6xl leading-none text-primary">{s.v}</div>
                <div className="mt-4 text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-background/70">{s.l}</div>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-6 md:gap-10 text-[11px] font-bold uppercase tracking-[0.3em]">
            <a href="/trainers" data-testid="home-proof-trainers-link" className="inline-flex items-center gap-2 text-background hover:text-primary transition-colors group">
              Meet The Coaches <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a href="/testimonials" data-testid="home-proof-testimonials-link" className="inline-flex items-center gap-2 text-background hover:text-primary transition-colors group">
              Read The Results <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section data-testid="home-mid-cta" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-tight">
            Ready to <span className="text-primary">BeGreat?</span>
          </h2>
          <a
            href="#inquiry"
            data-testid="home-mid-cta-btn"
            className="mt-10 inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-5 text-xs font-bold uppercase tracking-[0.3em] hover:bg-foreground hover:text-background transition-colors"
          >
            Train With Us <ArrowRight size={14} />
          </a>
        </div>
      </section>


      {/* REELS */}
      <section id="reels" data-testid="home-reels" className="py-20 md:py-28 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 mb-12 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">The Reel</span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                Motion. <span className="text-primary">Mechanics.</span> Mastery.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right">
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="home-instagram-cta"
                className="inline-flex items-center gap-2 px-5 py-3 border-2 border-background text-[10px] font-bold uppercase tracking-[0.25em] hover:bg-primary hover:border-primary transition-colors"
              >
                <Instagram size={14} /> {SITE.instagramHandle}
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
            {VIDEO_REELS.map((reel, i) => (
              <ReelCard key={reel.id} reel={reel} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="inquiry" data-testid="home-inquiry" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-x-0 gap-y-10 md:gap-16">
            <div className="col-span-12 md:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Player Info</span>
              </div>
              <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
                Ready to <br /><span className="text-primary">be great?</span>
              </h2>
              <p className="mt-6 text-foreground/75 text-base md:text-lg max-w-md">
                Send us the player's info and we'll get back fast with the next available session.
              </p>
              <div className="mt-10 space-y-5">
                <a href={SITE.phoneHref} data-testid="home-phone" className="flex items-center gap-4 group">
                  <div className="p-3 border border-foreground/30 group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors"><Phone size={16} /></div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">Call</div>
                    <div className="font-display text-2xl uppercase">{SITE.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${SITE.email}`} data-testid="home-email" className="flex items-center gap-4 group">
                  <div className="p-3 border border-foreground/30 group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors"><Mail size={16} /></div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">Email</div>
                    <div className="font-display text-2xl uppercase break-all">{SITE.email}</div>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="p-3 border border-foreground/30"><MapPin size={16} /></div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">Where</div>
                    <div className="font-display text-2xl uppercase">{SITE.location}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-12 md:col-span-7">
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
                onSubmit={onSubmit}
                data-testid="inquiry-form"
                className="bg-secondary/40 border border-foreground/15 p-6 md:p-10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Player First Name" name="first_name" value={form.first_name} onChange={onChange} required testid="input-first-name" />
                  <Field label="Player Last Name" name="last_name" value={form.last_name} onChange={onChange} required testid="input-last-name" />
                  <Field label="Phone Number" name="phone" type="tel" value={form.phone} onChange={onChange} required testid="input-phone" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="input-email" />
                </div>
                <div className="mt-6">
                  <label className="block text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground mb-2">Grade & Basketball Experience</label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={onChange}
                    required
                    rows={4}
                    data-testid="input-description"
                    placeholder="e.g. 8th grader, played AAU last year, working on left-hand finishing..."
                    className="w-full bg-transparent border-0 border-b-2 border-foreground/25 focus:border-primary outline-none py-3 text-base resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="inquiry-submit"
                  className="mt-8 group inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] hover:bg-primary disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "Sending..." : "Send Inquiry"}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="mt-4 text-xs text-muted-foreground">We reply within 24 hours. By submitting you agree to be contacted about training.</p>
              </motion.form>
            </div>
          </div>
        </div>
      </section>

      {/* AREA PLAYER OF THE YEAR — Press features */}
      <section data-testid="press-features" className="py-20 md:py-28 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 items-end mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-9">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Press · In Their Words</span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                The Boys & Girls <br />
                <span className="text-primary">Area Players of the Year</span> <br />
                trained with BeGreat.
              </h2>
              <p className="mt-6 max-w-2xl text-background/70 text-base md:text-lg">
                Read what was said about working with Isaac and the BeGreat team.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
            <motion.figure
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7 }}
              data-testid="press-cole"
              className="bg-background text-foreground border-l-4 border-primary"
            >
              <div className="p-5 md:p-7 border-b border-foreground/10">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-2">Boys · Area Player of the Year</div>
                <div className="font-display text-3xl md:text-4xl uppercase leading-none">Cole Jacobson · GFW</div>
              </div>
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_hoops-luxe/artifacts/bzyiztt6_Cole%20J%20Testimonial.jpg"
                  alt="Cole Jacobson press feature about training with Isaac Fink at BeGreat"
                  className="w-full h-auto block"
                />
              </div>
            </motion.figure>

            <motion.figure
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              data-testid="press-morgan"
              className="bg-background text-foreground border-l-4 border-primary"
            >
              <div className="p-5 md:p-7 border-b border-foreground/10">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-2">Girls · Area Player of the Year</div>
                <div className="font-display text-3xl md:text-4xl uppercase leading-none">Morgan Mathiowetz · Sleepy Eye St. Mary's</div>
              </div>
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_hoops-luxe/artifacts/m0qrxn12_Morgan%20M%20testimonial.png"
                  alt="Morgan Mathiowetz press feature in The Journal about training with BeGreat"
                  className="w-full h-auto block"
                />
              </div>
            </motion.figure>
          </div>

          <div className="mt-10 text-center text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-background/50">
            Featured in regional press · #BeGreat
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-0 border-b-2 border-foreground/25 focus:border-primary outline-none py-3 text-base"
      />
    </div>
  );
}
